import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../../i18n';

/**
 * App-wide English / Bahasa Melayu switcher.
 *
 * Self-contained dropdown (own open state, own outside-click handling) rather
 * than a Bootstrap dropdown: TopHeader.js uses Bootstrap 4 `data-toggle`
 * markup while the project ships Bootstrap 5 (`data-bs-toggle`), so relying on
 * that plumbing here would be fragile.
 *
 * variant="header"   — inline, for the navbar (renders a bare <li>)
 * variant="floating" — absolutely positioned top-right, for pre-auth pages
 */
const LanguageSwitcher = ({ variant = 'header' }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = useCallback(
    (code) => {
      i18n.changeLanguage(code); // detector persists to localStorage 'lang'
      setOpen(false);
    },
    [i18n]
  );

  const isFloating = variant === 'floating';

  // 'fixed' rather than 'absolute': the pre-auth pages have no positioned
  // ancestor, and adding one would disturb the existing .login-page layout.
  const wrapStyle = isFloating
    ? { position: 'fixed', top: 16, right: 20, zIndex: 1030 }
    : { position: 'relative', display: 'flex', alignItems: 'center' };

  // 34px / radius 4 to match .loggedasButton, so the two sit on one line in
  // the header with the same silhouette (see the header block in App.css).
  const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 34,
    padding: '0 10px',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    cursor: 'pointer',
    border: '1px solid rgba(0,0,0,.15)',
    borderRadius: 4,
    background: isFloating ? 'rgba(255,255,255,.9)' : 'transparent',
    color: 'inherit',
    whiteSpace: 'nowrap',
  };

  const menuStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 4,
    minWidth: 170,
    background: '#fff',
    border: '1px solid rgba(0,0,0,.15)',
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,.15)',
    padding: '4px 0',
    listStyle: 'none',
    margin: 0,
    zIndex: 1050,
  };

  const inner = (
    <div ref={ref} style={wrapStyle}>
      <button
        type="button"
        style={buttonStyle}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('language.label')}
        title={t('language.label')}
      >
        <i className="fa fa-globe" aria-hidden="true" />
        <span>{current.short}</span>
        <i className="fa fa-caret-down" aria-hidden="true" />
      </button>

      {open && (
        <ul style={menuStyle} role="listbox" aria-label={t('language.label')}>
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => select(l.code)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '7px 14px',
                    fontSize: 14,
                    border: 'none',
                    background: active ? 'rgba(0,0,0,.05)' : 'transparent',
                    fontWeight: active ? 600 : 400,
                    color: '#212529',
                    cursor: 'pointer',
                  }}
                >
                  {t(`language.${l.code}`)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return isFloating ? inner : <li className="nav-item">{inner}</li>;
};

export default LanguageSwitcher;
